/**
 * Spend and Earn Progress Bar - Secret Reveal Design
 *
 * Starts with single scale (0-tier1), then reveals second tier
 * with animated transition when first reward is reached.
 *
 * @class SpendAndEarn
 * @extends HTMLElement
 */

if (!customElements.get('spend-and-earn')) {
  class SpendAndEarn extends HTMLElement {
    static VERSION = 3;
    static STORAGE_KEY = `spend-and-earn-state-v${SpendAndEarn.VERSION}`;
    static DEBUG = false;
    static ENABLE_HAPTICS = true;

    // Track cart update state to prevent double animations
    static isCartUpdating = false;
    static cartUpdateTimestamp = 0;

    static STATES = {
      IDLE: 'idle',
      ANIMATING: 'animating',
      CELEBRATING: 'celebrating',
      TRANSITIONING: 'transitioning'
    };

    static TRANSITIONS = {
      idle: ['animating', 'celebrating', 'transitioning'],
      animating: ['idle', 'celebrating', 'transitioning'],
      celebrating: ['idle', 'transitioning'],
      transitioning: ['idle', 'animating']
    };

    static SCALE_MODES = {
      SINGLE: 'single',
      DUAL: 'dual'
    };

    static TIMING = {
      // Phase 1: Progress
      FILL_DURATION: 500,  // Snappier fill (was 600ms)
      FILL_DURATION_SLOW: 1200,  // Reduced from 1400ms

      // Phase 2: Celebration (sequential - confetti must complete before reposition)
      MARKER_CELEBRATE: 500,  // Tightened bounce (was 600ms)
      CELEBRATION_PAUSE: 600,  // Reduced from 800ms - just enough for confetti burst

      // Phase 3: List transitions (with overlapping handoff for smooth flow)
      CHECK_POP: 350,  // Snappier check pop (was 400ms)
      CHECK_HOLD: 400,  // "Look what you got!" moment - reduced from 700ms for better flow
      LOCKED_EXIT: 800,  // Smoother exit (reduced from 1000ms)
      LOCKED_EXIT_START_DELAY: 200,  // Minimal breathing room (was 500ms) - tighter transition
      NEXT_LOCKED_ENTER_DELAY: 300,  // Quicker reveal (was 400ms)
      NEXT_LOCKED_ENTER_DURATION: 900,  // Faster entrance (was 1200ms)
      UNLOCKED_ENTER: 800,  // Matches new LOCKED_EXIT for perfect sync
      UNLOCKED_ENTER_OVERLAP: 0,  // Start reward at SAME TIME as locked exit (no offset)

      // Phase 4: Scale transition (starts after celebration completes)
      SCALE_TRANSITION: 500,  // Snappier (was 600ms)
      SCALE_DELAY: 600,  // Reduced wait (was 800ms)

      // Legacy timings (kept for other methods)
      REVEAL_DURATION: 500,  // Tightened (was 600ms)
      MARKER_SLIDE_DURATION: 450,  // Slightly faster (was 500ms)
      UNLOCK_CHECK_DURATION: 350,  // Tightened (was 400ms)
      UNLOCK_EXIT_DELAY: 300,  // Reduced (was 400ms)
      UNLOCK_EXIT_DURATION: 500,  // Faster (was 600ms)
      REWARD_ENTER_DELAY: 150,  // Quicker (was 200ms)
      REWARD_ENTER_DURATION: 700,  // Slightly faster (was 800ms)
      NEXT_GIFT_ENTER_DURATION: 700,  // Tightened (was 800ms)

      EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',
      EASE_OUT_SLOW: 'cubic-bezier(0.16, 1, 0.3, 1)',
      BOUNCE_EASING: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      SPRING_EASING: 'cubic-bezier(0.34, 1.15, 0.64, 1)'
    };

    static APPROACH_THRESHOLD = 0.85;

    constructor() {
      super();

      this.state = SpendAndEarn.STATES.IDLE;
      this.scaleMode = SpendAndEarn.SCALE_MODES.SINGLE;
      this.animationAbortController = null;
      this.debounceTimer = null;

      this.elements = {};

      this.config = {
        tier1Threshold: 8000,
        tier2Threshold: 13000,
        tier1Reward: '',
        tier2Reward: '',
        tier1Position: 61.54
      };

      this.handleCartUpdateStart = this.handleCartUpdateStart.bind(this);
      this.handleCartUpdate = this.onCartUpdate.bind(this);
      this.handleCartUpdateError = this.handleCartUpdateError.bind(this);
    }

    connectedCallback() {
      this.cacheElements();
      this.loadConfig();
      this.initialize();
      this.attachEventListeners();
      this.attachMicroInteractions();
    }

    disconnectedCallback() {
      this.detachEventListeners();
      this.cancelAnimations();
    }

    log(...args) {
      if (SpendAndEarn.DEBUG) {
        console.log('[SpendAndEarn]', ...args);
      }
    }

    warn(...args) {
      if (SpendAndEarn.DEBUG) {
        console.warn('[SpendAndEarn]', ...args);
      }
    }

    cacheElements() {
      this.elements = {
        message: this.querySelector('[data-message]'),
        track: this.querySelector('[data-track]'),
        fill: this.querySelector('[data-fill]'),
        particles: this.querySelector('[data-particles]'),
        tier1Marker: this.querySelector('[data-tier1-marker]'),
        tier2Marker: this.querySelector('[data-tier2-marker]'),
        rewardTier1: this.querySelector('[data-reward-tier1]'),
        rewardTier2: this.querySelector('[data-reward-tier2]'),
        confetti: this.querySelector('[data-confetti]'),
        nextGift: this.querySelector('[data-next-gift]'),
        nextGiftTier1: this.querySelector('[data-next-gift-tier1]'),
        nextGiftTier2: this.querySelector('[data-next-gift-tier2]')
      };
    }

    loadConfig() {
      this.config = {
        ...this.config,
        tier1Threshold: parseInt(this.dataset.tier1Threshold, 10) || 8000,
        tier2Threshold: parseInt(this.dataset.tier2Threshold, 10) || 13000,
        tier1Reward: this.dataset.tier1Reward || '',
        tier2Reward: this.dataset.tier2Reward || '',
        tier1Position: parseFloat(this.dataset.tier1Position) || 61.54
      };

      this.style.setProperty('--tier1-position', `${this.config.tier1Position}%`);
    }

    async initialize() {
      const currentTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      const previousState = this.loadSessionState();
      const currentTier = this.getTier(currentTotal);

      if (currentTier >= 1) {
        this.setScaleMode(SpendAndEarn.SCALE_MODES.DUAL);
      } else {
        this.setScaleMode(SpendAndEarn.SCALE_MODES.SINGLE);
      }

      const shouldCelebrateTier1 = previousState &&
        previousState.tier === 0 &&
        currentTier >= 1 &&
        previousState.scaleMode === SpendAndEarn.SCALE_MODES.SINGLE;

      const shouldCelebrateTier2 = previousState &&
        previousState.tier === 1 &&
        currentTier === 2;

      this.saveSessionState(currentTotal, currentTier);

      if (shouldCelebrateTier1) {
        this.setScaleMode(SpendAndEarn.SCALE_MODES.SINGLE);
        this.setProgressInstant(previousState.total);
        this.updateMessage(previousState.total);
        await this.animateToTier1WithReveal(currentTotal);
      } else if (shouldCelebrateTier2) {
        this.setProgressInstant(previousState.total);
        this.updateMessage(previousState.total);
        await this.animateToWithCelebration(currentTotal, 2);
      } else if (previousState && previousState.total !== currentTotal) {
        this.setProgressInstant(previousState.total);
        await this.animateTo(currentTotal);
      } else {
        this.setProgressInstant(currentTotal);
        this.updateMessage(currentTotal, false);
        this.updateMarkers(currentTotal);
        this.updateRewardsList(currentTotal);
      }
    }

    attachEventListeners() {
      document.addEventListener('line-item:change:start', this.handleCartUpdateStart);
      document.addEventListener('line-item:change:end', this.handleCartUpdate);
      document.addEventListener('line-item:change:error', this.handleCartUpdateError);
    }

    detachEventListeners() {
      document.removeEventListener('line-item:change:start', this.handleCartUpdateStart);
      document.removeEventListener('line-item:change:end', this.handleCartUpdate);
      document.removeEventListener('line-item:change:error', this.handleCartUpdateError);
    }

    handleCartUpdateStart() {
      // Set flag to indicate cart update is in progress
      // This prevents double animations when component is recreated
      SpendAndEarn.isCartUpdating = true;
      SpendAndEarn.cartUpdateTimestamp = Date.now();
    }

    handleCartUpdateError() {
      // Reset flag if cart update fails - prevents permanent animation blocking
      SpendAndEarn.isCartUpdating = false;
    }

    attachMicroInteractions() {
      // Hover effects on locked gifts - subtle scale preview
      const nextGifts = [this.elements.nextGiftTier1, this.elements.nextGiftTier2];
      nextGifts.forEach(gift => {
        if (!gift) return;

        gift.addEventListener('mouseenter', () => {
          const image = gift.querySelector('.spend-and-earn__next-gift-image');
          if (image && !gift.classList.contains('spend-and-earn__next-gift-item--unlocked-visible')) {
            image.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease, filter 0.3s ease';
            image.style.transform = 'scale(1.05)';
          }
        });

        gift.addEventListener('mouseleave', () => {
          const image = gift.querySelector('.spend-and-earn__next-gift-image');
          if (image) {
            image.style.transform = 'scale(1)';
          }
        });
      });

      // Progress bar hover shimmer - plays to completion even after mouse leaves
      if (this.elements.track) {
        let isAnimating = false;

        this.elements.track.addEventListener('mouseenter', () => {
          // Don't retrigger if already animating
          if (isAnimating) return;

          isAnimating = true;
          this.triggerFillAnimation(3000);

          // Reset flag after animation completes
          setTimeout(() => {
            isAnimating = false;
          }, 3000);
        });
      }
    }

    // Haptic feedback patterns
    vibrate(pattern) {
      if (!SpendAndEarn.ENABLE_HAPTICS) return;
      if (!('vibrate' in navigator)) return;

      try {
        switch (pattern) {
          case 'unlock':
            navigator.vibrate([50, 30, 50]); // Double-tap celebration
            break;
          case 'check':
            navigator.vibrate(30); // Single gentle tap
            break;
          case 'progress':
            navigator.vibrate(10); // Subtle tick
            break;
        }
      } catch (e) {
        // Vibration not supported or failed
      }
    }

    async onCartUpdate(event) {
      const cart = event.detail?.cart || {};
      const newTotal = cart.total_price || 0;
      const previousTotal = parseInt(this.dataset.cartTotal, 10) || 0;

      // Check if component was just recreated (within 500ms of cart update start)
      // If so, initialize() already handled the animation - skip onCartUpdate
      const timeSinceCartUpdate = Date.now() - SpendAndEarn.cartUpdateTimestamp;
      const wasJustRecreated = SpendAndEarn.isCartUpdating || timeSinceCartUpdate < 500;

      if (wasJustRecreated) {
        SpendAndEarn.isCartUpdating = false;
        return;
      }

      if (newTotal === previousTotal) {
        return;
      }

      const previousTier = this.getTier(previousTotal);
      const currentTier = this.getTier(newTotal);

      this.dataset.cartTotal = newTotal;
      this.saveSessionState(newTotal, currentTier);

      // Clear any pending debounced updates
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      // Tier unlocks should be immediate (exciting moments!)
      const isTierUnlock = (previousTier === 0 && currentTier >= 1) ||
                          (previousTier === 1 && currentTier === 2) ||
                          (currentTier === 0 && this.scaleMode === SpendAndEarn.SCALE_MODES.DUAL);

      if (isTierUnlock) {
        this.cancelAnimations();

        if (previousTier === 0 && currentTier >= 1 && this.scaleMode === SpendAndEarn.SCALE_MODES.SINGLE) {
          await this.animateToTier1WithReveal(newTotal);
        } else if (previousTier === 1 && currentTier === 2) {
          await this.animateToWithCelebration(newTotal, 2);
        } else if (currentTier === 0 && this.scaleMode === SpendAndEarn.SCALE_MODES.DUAL) {
          await this.transitionToSingleScale(newTotal);
        }
        // Reset flag after tier unlock animation completes
        SpendAndEarn.isCartUpdating = false;
      } else {
        // Debounce regular progress updates (quantity changes, price adjustments)
        this.debounceTimer = setTimeout(async () => {
          this.cancelAnimations();
          await this.animateTo(newTotal);
          this.debounceTimer = null;
          // Reset flag after regular update animation completes
          SpendAndEarn.isCartUpdating = false;
        }, 400);
      }
    }

    canTransitionTo(newState) {
      const allowed = SpendAndEarn.TRANSITIONS[this.state];
      return allowed?.includes(newState) ?? false;
    }

    transitionTo(newState) {
      if (!this.canTransitionTo(newState)) {
        this.warn(`Invalid state transition ${this.state} -> ${newState}`);
        return false;
      }
      this.state = newState;
      return true;
    }

    setScaleMode(mode) {
      this.scaleMode = mode;
      this.dataset.scaleMode = mode;
    }

    loadSessionState() {
      try {
        const stored = sessionStorage.getItem(SpendAndEarn.STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    }

    saveSessionState(total, tier) {
      try {
        sessionStorage.setItem(SpendAndEarn.STORAGE_KEY, JSON.stringify({
          total,
          tier,
          scaleMode: this.scaleMode,
          timestamp: Date.now()
        }));
      } catch {
        // Ignore storage errors
      }
    }

    getTier(total) {
      if (total >= this.config.tier2Threshold) return 2;
      if (total >= this.config.tier1Threshold) return 1;
      return 0;
    }

    getApproachingTier(total) {
      const tier1Progress = total / this.config.tier1Threshold;
      const tier2Progress = total / this.config.tier2Threshold;

      if (tier1Progress >= SpendAndEarn.APPROACH_THRESHOLD && tier1Progress < 1) {
        return 1;
      }
      if (tier2Progress >= SpendAndEarn.APPROACH_THRESHOLD && tier2Progress < 1) {
        return 2;
      }
      return 0;
    }

    calculateProgress(total) {
      if (this.scaleMode === SpendAndEarn.SCALE_MODES.SINGLE) {
        const progress = (total / this.config.tier1Threshold) * 100;
        return Math.min(100, Math.max(0, progress));
      } else {
        const progress = (total / this.config.tier2Threshold) * 100;
        return Math.min(100, Math.max(0, progress));
      }
    }

    cancelAnimations() {
      if (this.animationAbortController) {
        this.animationAbortController.abort();
        this.animationAbortController = null;
      }
      this.state = SpendAndEarn.STATES.IDLE;
    }

    createAbortController() {
      this.cancelAnimations();
      this.animationAbortController = new AbortController();
      return this.animationAbortController.signal;
    }

    async animateElement(element, property, fromValue, toValue, duration, easing = SpendAndEarn.TIMING.EASING) {
      if (!element) return;

      const originalTransition = element.style.transition;
      element.style.transition = 'none';

      element.style[property] = fromValue;
      void element.offsetWidth;

      const animation = element.animate(
        [
          { [property]: fromValue },
          { [property]: toValue }
        ],
        {
          duration,
          easing,
          fill: 'forwards'
        }
      );

      try {
        await animation.finished;
        element.style[property] = toValue;
      } catch (error) {
        if (error.name !== 'AbortError') throw error;
      } finally {
        animation.cancel();
        element.style.transition = originalTransition;
      }
    }

    delay(ms, signal) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, ms);
        if (signal) {
          signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new DOMException('Aborted', 'AbortError'));
          });
        }
      });
    }

    setProgressInstant(total) {
      const progress = this.calculateProgress(total);
      if (this.elements.fill) {
        this.elements.fill.style.width = `${progress}%`;
      }
      this.updateMessage(total, false);
      this.updateMarkers(total);
      this.updateNextGiftPreview(total);
      this.updateRewardsList(total);
    }

    async animateTo(total) {
      if (!this.transitionTo(SpendAndEarn.STATES.ANIMATING)) return;

      const signal = this.createAbortController();

      try {
        const targetProgress = this.calculateProgress(total);
        const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

        if (this.elements.fill && currentProgress !== targetProgress) {
          if (targetProgress > currentProgress) {
            this.spawnGoldParticles(6);
            this.triggerFillAnimation();
            this.vibrate('progress'); // Subtle progress haptic
          }

          await this.animateElement(
            this.elements.fill,
            'width',
            `${currentProgress}%`,
            `${targetProgress}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        this.updateUI(total);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.warn('Animation error', error);
        }
      } finally {
        if (this.state === SpendAndEarn.STATES.ANIMATING) {
          this.state = SpendAndEarn.STATES.IDLE;
        }
      }
    }

    triggerFillAnimation(duration = 3000) {
      if (!this.elements.fill) return;

      // Remove any existing animation classes
      this.elements.fill.classList.remove('spend-and-earn__fill--animating');
      this.elements.fill.classList.remove('spend-and-earn__fill--animating-slow');

      // Force reflow to ensure clean animation start
      void this.elements.fill.offsetWidth;

      // Start the shimmer animation with seamless tileable gradient
      const animClass = duration > 3000 ? 'spend-and-earn__fill--animating-slow' : 'spend-and-earn__fill--animating';
      this.elements.fill.classList.add(animClass);

      // Clean up animation class after it completes
      setTimeout(() => {
        if (!this.elements.fill) return;
        this.elements.fill.classList.remove('spend-and-earn__fill--animating');
        this.elements.fill.classList.remove('spend-and-earn__fill--animating-slow');
      }, duration);
    }

    async animateToTier1WithReveal(total) {
      if (!this.transitionTo(SpendAndEarn.STATES.ANIMATING)) return;

      const signal = this.createAbortController();

      try {
        const currentTier = this.getTier(total);
        const unlockBothTiers = currentTier >= 2;

        // PHASE 1: Fill progress (0-600ms)
        await this.animateProgressFill(signal);

        // PHASE 2: Celebrate (600-1400ms) - BLOCKING, confetti completes
        await this.celebrateUnlock(1, signal);

        // PHASE 3 & 4: Parallel list + scale (both need to happen)
        // List starts at 800ms, scale starts at 1400ms (staggered)
        await Promise.all([
          this.animateListHandoff(unlockBothTiers, signal),
          this.animateScaleTransition(total, signal)
        ]);

        // If unlocking both tiers, handle tier 2 unlock now
        if (unlockBothTiers) {
          await this.celebrateUnlock(2, signal);
          await this.animateSecondTierHandoff(signal);
        }

        this.updateMessage(total);
        this.updateMarkers(total);

      } catch (error) {
        if (error.name !== 'AbortError') {
          this.warn('Animation error', error);
        }
      } finally {
        this.state = SpendAndEarn.STATES.IDLE;
      }
    }

    // PHASE 1: Progress Fill (0-600ms)
    async animateProgressFill(signal) {
      const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

      if (this.elements.fill && currentProgress < 100) {
        this.spawnGoldParticles(10);
        this.triggerFillAnimation(6000);

        await this.animateElement(
          this.elements.fill,
          'width',
          `${currentProgress}%`,
          '100%',
          SpendAndEarn.TIMING.FILL_DURATION
        );
      }
    }

    // PHASE 2: Celebration (600-1400ms) - Marker unlocks, confetti explodes and completes
    async celebrateUnlock(tier, signal) {
      const marker = tier === 1 ? this.elements.tier1Marker : this.elements.tier2Marker;

      // Unlock marker visually (instant)
      if (marker) {
        marker.classList.remove('spend-and-earn__marker--locked');
        marker.classList.remove('spend-and-earn__marker--approaching');
        marker.classList.add('spend-and-earn__marker--unlocked');
        marker.classList.add('spend-and-earn__marker--celebrating');

        // Remove celebrating class after bounce animation
        setTimeout(() => {
          marker.classList.remove('spend-and-earn__marker--celebrating');
        }, SpendAndEarn.TIMING.MARKER_CELEBRATE);
      }

      // Haptic feedback
      this.vibrate('unlock');

      this.spawnGoldParticles(12);
      this.transitionTo(SpendAndEarn.STATES.CELEBRATING);

      // Trigger confetti immediately
      this.triggerConfetti(tier);

      // Wait for celebration to complete (confetti finishes)
      await this.delay(SpendAndEarn.TIMING.CELEBRATION_PAUSE, signal);
    }

    /**
     * Core unlock animation - handles locked → unlocked transition
     * Single source of truth for all tier unlock sequences
     *
     * @param {Object} config - Animation configuration
     * @param {HTMLElement} config.lockedElement - Element in locked list
     * @param {HTMLElement} config.rewardElement - Element in unlocked list
     * @param {number} config.initialDelay - Breathing room before animation (200ms or 400ms)
     * @param {boolean} config.collapseContainer - Whether to collapse the next gift container (final tier)
     * @param {Function} config.onCleanup - Async callback after exit completes
     * @param {AbortSignal} signal - Cancellation signal
     */
    async _performUnlockAnimation(config, signal) {
      const { lockedElement, rewardElement, initialDelay, collapseContainer, onCleanup } = config;

      // Phase 1: Breathing room after celebration
      await this.delay(initialDelay, signal);

      // Phase 2: Show check - "You unlocked it!"
      if (lockedElement) {
        lockedElement.classList.add('spend-and-earn__next-gift-item--unlocked-visible');

        // Haptic feedback for check appearance
        this.vibrate('check');
      }

      // Phase 3: Hold unlocked state - "Look what you got!" moment
      await this.delay(SpendAndEarn.TIMING.CHECK_POP + SpendAndEarn.TIMING.CHECK_HOLD, signal);

      // Phase 4: Start exit animation
      if (lockedElement) {
        lockedElement.classList.add('spend-and-earn__next-gift-item--unlocking');
      }

      // If this is the final tier, collapse the container simultaneously
      if (collapseContainer && this.elements.nextGift) {
        this.elements.nextGift.classList.add('spend-and-earn__next-gift--hidden');
      }

      // Phase 5: Start reward entering (overlapped with exit)
      await this.delay(SpendAndEarn.TIMING.LOCKED_EXIT_START_DELAY, signal);
      if (rewardElement) {
        rewardElement.classList.add('spend-and-earn__reward--unlocked');
        rewardElement.classList.add('spend-and-earn__reward--entering');
      }

      // Phase 6: Wait for exit animation to complete
      await this.delay(SpendAndEarn.TIMING.LOCKED_EXIT, signal);

      // Phase 7: Custom cleanup logic (varies per use case)
      if (onCleanup) {
        await onCleanup(lockedElement, rewardElement, signal);
      }

      // Phase 8: Wait for reward to settle in unlocked list
      const rewardSettleTime = SpendAndEarn.TIMING.UNLOCKED_ENTER -
        (SpendAndEarn.TIMING.LOCKED_EXIT_START_DELAY + SpendAndEarn.TIMING.LOCKED_EXIT);
      if (rewardSettleTime > 0) {
        await this.delay(rewardSettleTime, signal);
      }

      // Phase 9: Finalize reward (remove entering class, add revealed)
      if (rewardElement) {
        rewardElement.classList.add('spend-and-earn__reward--revealed');
        rewardElement.classList.remove('spend-and-earn__reward--entering');
      }
    }

    // PHASE 3: List Handoff - Smooth overlapping transitions
    async animateListHandoff(unlockBothTiers, signal) {
      // Use core unlock animation with tier 1 specific cleanup
      await this._performUnlockAnimation({
        lockedElement: this.elements.nextGiftTier1,
        rewardElement: this.elements.rewardTier1,
        initialDelay: 200,  // Breathing room after celebration
        onCleanup: async (lockedElement, rewardElement, signal) => {
          // Clean up tier 1 locked item
          if (lockedElement) {
            lockedElement.classList.remove('spend-and-earn__next-gift-item--visible');
            lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
            lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocking');
          }

          // Suspenseful pause - empty locked list moment builds anticipation
          await this.delay(SpendAndEarn.TIMING.NEXT_LOCKED_ENTER_DELAY, signal);

          // Reveal tier 2 with dramatic entrance (always show, even if unlocking both)
          if (this.elements.nextGiftTier2) {
            this.elements.nextGiftTier2.classList.add('spend-and-earn__next-gift-item--visible');
            this.elements.nextGiftTier2.classList.add('spend-and-earn__next-gift-item--entering');
          }

          // Wait for tier 2 entering animation to complete (1200ms duration from CSS)
          await this.delay(SpendAndEarn.TIMING.NEXT_LOCKED_ENTER_DURATION + 100, signal);
          if (this.elements.nextGiftTier2) {
            this.elements.nextGiftTier2.classList.remove('spend-and-earn__next-gift-item--entering');
          }
        }
      }, signal);
    }

    // PHASE 4: Scale Transition (1400-2600ms) - Marker repositions, scale expands
    async animateScaleTransition(total, signal) {
      // Wait for celebration to complete before repositioning
      await this.delay(SpendAndEarn.TIMING.SCALE_DELAY, signal);

      const tier1Marker = this.elements.tier1Marker;
      const tier2Marker = this.elements.tier2Marker;

      this.transitionTo(SpendAndEarn.STATES.TRANSITIONING);

      // Slide marker from 100% position to tier1Position (61.5%)
      if (tier1Marker) {
        const trackRect = this.elements.track.getBoundingClientRect();
        const markerRect = tier1Marker.getBoundingClientRect();

        const startX = markerRect.left - trackRect.left;
        const endX = (this.config.tier1Position / 100) * trackRect.width;

        const slideAnimation = tier1Marker.animate([
          {
            left: `${startX}px`,
            right: 'auto',
            transform: 'translate(-50%, -50%)'
          },
          {
            left: `${endX}px`,
            right: 'auto',
            transform: 'translate(-50%, -50%)'
          }
        ], {
          duration: SpendAndEarn.TIMING.SCALE_TRANSITION,
          easing: SpendAndEarn.TIMING.EASING,
          fill: 'forwards'
        });

        // Rescale progress bar simultaneously
        if (this.elements.fill) {
          this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.SCALE_TRANSITION}ms ${SpendAndEarn.TIMING.EASING}`;
          this.elements.fill.style.width = `${this.config.tier1Position}%`;
        }

        await slideAnimation.finished;

        slideAnimation.cancel();
        if (this.elements.fill) {
          this.elements.fill.style.transition = '';
        }
      }

      // Hide tier 2 marker initially
      if (tier2Marker) {
        tier2Marker.style.opacity = '0';
      }

      // Switch to dual scale mode
      this.setScaleMode(SpendAndEarn.SCALE_MODES.DUAL);

      // Crossfade message
      if (this.elements.message) {
        this.elements.message.style.transition = 'opacity 0.3s ease';
        this.elements.message.style.opacity = '0';
      }

      await this.delay(300, signal);

      if (this.elements.message) {
        this.updateMessage(total, false);
        this.elements.message.style.opacity = '1';
      }

      // Animate progress bar to current dual-scale progress
      const dualProgress = this.calculateProgress(total);
      if (dualProgress > this.config.tier1Position && this.elements.fill) {
        this.elements.fill.style.width = `${this.config.tier1Position}%`;
        void this.elements.fill.offsetWidth;

        this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.FILL_DURATION_SLOW}ms ${SpendAndEarn.TIMING.EASE_OUT_SLOW}`;
        this.elements.fill.style.width = `${dualProgress}%`;

        await this.delay(900, signal);

        // Fade in tier 2 marker
        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.style.transition = 'opacity 0.5s ease-out';
          this.elements.tier2Marker.style.opacity = '1';
        }

        await this.delay(SpendAndEarn.TIMING.FILL_DURATION_SLOW - 900, signal);

        this.elements.fill.style.transition = '';
        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.style.transition = '';
        }
      } else {
        // Just fade in tier 2 marker
        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.style.transition = 'opacity 0.5s ease-out';
          this.elements.tier2Marker.style.opacity = '1';
          await this.delay(500, signal);
          this.elements.tier2Marker.style.transition = '';
        }
      }
    }

    // Handle tier 2 unlock when both tiers unlock at once
    async animateSecondTierHandoff(signal) {
      // Tier 2 is already visible and entering from animateListHandoff
      // Use core unlock animation with tier 2 dual-unlock specific cleanup
      await this._performUnlockAnimation({
        lockedElement: this.elements.nextGiftTier2,
        rewardElement: this.elements.rewardTier2,
        initialDelay: 400,  // "Here's the next one!" settle moment - longer than tier 1
        collapseContainer: true,  // Collapse container in sync with exit animation
        onCleanup: async (lockedElement, rewardElement, signal) => {
          // Clean up tier 2 locked item (container already collapsed in Phase 4)
          if (lockedElement) {
            lockedElement.classList.remove('spend-and-earn__next-gift-item--visible');
            lockedElement.classList.remove('spend-and-earn__next-gift-item--entering');
            lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
            lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocking');
          }
        }
      }, signal);
    }

    async animateListTransition(unlockBothTiers, signal) {
      // Phase 1: Show tier 1 unlocked (check appears, colors change)
      if (this.elements.nextGiftTier1) {
        this.elements.nextGiftTier1.classList.add('spend-and-earn__next-gift-item--unlocked-visible');
      }

      // Wait for check animation
      await this.delay(SpendAndEarn.TIMING.UNLOCK_CHECK_DURATION, signal);

      // Phase 2: Start tier 1 exit and tier 2 entrance simultaneously
      if (this.elements.nextGiftTier1) {
        this.elements.nextGiftTier1.classList.add('spend-and-earn__next-gift-item--unlocking');
      }

      if (this.elements.nextGiftTier2) {
        this.elements.nextGiftTier2.classList.add('spend-and-earn__next-gift-item--visible');
        this.elements.nextGiftTier2.classList.add('spend-and-earn__next-gift-item--entering');
      }

      // Phase 3: Start reward entering (slight delay for visual flow)
      await this.delay(SpendAndEarn.TIMING.REWARD_ENTER_DELAY, signal);
      if (this.elements.rewardTier1) {
        this.elements.rewardTier1.classList.add('spend-and-earn__reward--unlocked');
        this.elements.rewardTier1.classList.add('spend-and-earn__reward--entering');
      }

      // Wait for unlock exit to complete
      await this.delay(SpendAndEarn.TIMING.UNLOCK_EXIT_DELAY + SpendAndEarn.TIMING.UNLOCK_EXIT_DURATION, signal);

      // Clean up tier 1
      if (this.elements.nextGiftTier1) {
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--visible');
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--unlocking');
      }

      // Clean up tier 2 entering animation
      if (this.elements.nextGiftTier2 && !unlockBothTiers) {
        this.elements.nextGiftTier2.classList.remove('spend-and-earn__next-gift-item--entering');
      }

      // Wait for reward enter animation and finalize
      await this.delay(SpendAndEarn.TIMING.REWARD_ENTER_DURATION - SpendAndEarn.TIMING.UNLOCK_EXIT_DELAY - SpendAndEarn.TIMING.UNLOCK_EXIT_DURATION, signal);
      if (this.elements.rewardTier1) {
        this.elements.rewardTier1.classList.add('spend-and-earn__reward--revealed');
        this.elements.rewardTier1.classList.remove('spend-and-earn__reward--entering');
      }
    }

    async performDualScaleReveal(total, signal) {
      const tier1Marker = this.elements.tier1Marker;
      const tier2Marker = this.elements.tier2Marker;

      this.transitionTo(SpendAndEarn.STATES.TRANSITIONING);

      if (tier1Marker) {
        const trackRect = this.elements.track.getBoundingClientRect();
        const markerRect = tier1Marker.getBoundingClientRect();

        const startX = markerRect.left - trackRect.left;
        const endX = (this.config.tier1Position / 100) * trackRect.width;

        const slideAnimation = tier1Marker.animate([
          {
            left: `${startX}px`,
            right: 'auto',
            transform: 'translate(-50%, -50%)'
          },
          {
            left: `${endX}px`,
            right: 'auto',
            transform: 'translate(-50%, -50%)'
          }
        ], {
          duration: SpendAndEarn.TIMING.REVEAL_DURATION,
          easing: SpendAndEarn.TIMING.EASING,
          fill: 'forwards'
        });

        if (this.elements.fill) {
          this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.REVEAL_DURATION}ms ${SpendAndEarn.TIMING.EASING}`;
          this.elements.fill.style.width = `${this.config.tier1Position}%`;
        }

        await slideAnimation.finished;

        slideAnimation.cancel();
        if (this.elements.fill) {
          this.elements.fill.style.transition = '';
        }
      }

      if (tier2Marker) {
        tier2Marker.style.opacity = '0';
      }

      this.setScaleMode(SpendAndEarn.SCALE_MODES.DUAL);

      if (this.elements.message) {
        this.elements.message.style.transition = 'opacity 0.3s ease';
        this.elements.message.style.opacity = '0';
      }

      await this.delay(300, signal);

      if (this.elements.message) {
        this.updateMessage(total, false);
        this.elements.message.style.opacity = '1';
      }

      // Animate progress bar if needed
      const dualProgress = this.calculateProgress(total);
      if (dualProgress > this.config.tier1Position && this.elements.fill) {
        this.elements.fill.style.width = `${this.config.tier1Position}%`;
        void this.elements.fill.offsetWidth;

        this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.FILL_DURATION_SLOW}ms ${SpendAndEarn.TIMING.EASE_OUT_SLOW}`;
        this.elements.fill.style.width = `${dualProgress}%`;

        await this.delay(900, signal);

        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.style.transition = 'opacity 0.5s ease-out';
          this.elements.tier2Marker.style.opacity = '1';
        }

        await this.delay(SpendAndEarn.TIMING.FILL_DURATION_SLOW - 900, signal);

        this.elements.fill.style.transition = '';
        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.style.transition = '';
        }
      } else {
        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.style.transition = 'opacity 0.5s ease-out';
          this.elements.tier2Marker.style.opacity = '1';
          await this.delay(500, signal);
          this.elements.tier2Marker.style.transition = '';
        }
      }
    }

    async revealDualScale(total, signal) {
      const tier1Marker = this.elements.tier1Marker;
      const tier2Marker = this.elements.tier2Marker;

      if (tier1Marker) {
        const trackRect = this.elements.track.getBoundingClientRect();
        const markerRect = tier1Marker.getBoundingClientRect();

        const startX = markerRect.left - trackRect.left;
        const endX = (this.config.tier1Position / 100) * trackRect.width;

        const slideAnimation = tier1Marker.animate([
          {
            left: `${startX}px`,
            right: 'auto',
            transform: 'translate(-50%, -50%)'
          },
          {
            left: `${endX}px`,
            right: 'auto',
            transform: 'translate(-50%, -50%)'
          }
        ], {
          duration: SpendAndEarn.TIMING.REVEAL_DURATION,
          easing: SpendAndEarn.TIMING.EASING,
          fill: 'forwards'
        });

        if (this.elements.fill) {
          this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.REVEAL_DURATION}ms ${SpendAndEarn.TIMING.EASING}`;
          this.elements.fill.style.width = `${this.config.tier1Position}%`;
        }

        await slideAnimation.finished;

        slideAnimation.cancel();
        if (this.elements.fill) {
          this.elements.fill.style.transition = '';
        }
      }

      if (tier2Marker) {
        tier2Marker.style.opacity = '0';
      }

      this.setScaleMode(SpendAndEarn.SCALE_MODES.DUAL);

      if (this.elements.message) {
        this.elements.message.style.transition = 'opacity 0.3s ease';
        this.elements.message.style.opacity = '0';
      }

      await this.delay(300, signal);

      if (this.elements.message) {
        this.updateMessage(total, false);
        this.elements.message.style.opacity = '1';
      }
    }

    async transitionToSingleScale(total) {
      if (!this.transitionTo(SpendAndEarn.STATES.TRANSITIONING)) return;

      const signal = this.createAbortController();

      try {
        const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

        if (this.elements.fill && currentProgress > this.config.tier1Position) {
          await this.animateElement(
            this.elements.fill,
            'width',
            `${currentProgress}%`,
            `${this.config.tier1Position}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        this.setScaleMode(SpendAndEarn.SCALE_MODES.SINGLE);

        const singleProgress = this.calculateProgress(total);

        if (this.elements.fill) {
          this.elements.fill.style.width = '100%';
          await this.animateElement(
            this.elements.fill,
            'width',
            '100%',
            `${singleProgress}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        this.updateUI(total);

      } catch (error) {
        if (error.name !== 'AbortError') {
          this.warn('Animation error', error);
        }
      } finally {
        this.state = SpendAndEarn.STATES.IDLE;
      }
    }

    async animateToWithCelebration(total, celebrateTier) {
      if (!this.transitionTo(SpendAndEarn.STATES.ANIMATING)) return;

      const signal = this.createAbortController();

      try {
        const targetProgress = this.calculateProgress(total);
        const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

        if (this.elements.fill && currentProgress < 100) {
          this.spawnGoldParticles(10);
          this.triggerFillAnimation();

          await this.animateElement(
            this.elements.fill,
            'width',
            `${currentProgress}%`,
            '100%',
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        // Unlock tier 2 marker
        if (this.elements.tier2Marker) {
          this.elements.tier2Marker.classList.remove('spend-and-earn__marker--locked');
          this.elements.tier2Marker.classList.remove('spend-and-earn__marker--approaching');
          this.elements.tier2Marker.classList.add('spend-and-earn__marker--unlocked');
        }

        this.spawnGoldParticles(12);

        // Trigger confetti immediately
        this.triggerConfetti(celebrateTier);

        this.transitionTo(SpendAndEarn.STATES.CELEBRATING);

        // Wait for celebration to complete FIRST (like Tier 1), then unlock
        await this.celebrateTier(celebrateTier, signal);

        // Now start unlock animation (sequential, not parallel)
        await this.animateUnlock(celebrateTier, signal);

        this.updateMessage(total);
        this.updateMarkers(total);

      } catch (error) {
        if (error.name !== 'AbortError') {
          this.warn('Animation error', error);
        }
      } finally {
        this.state = SpendAndEarn.STATES.IDLE;
      }
    }

    async celebrateTier(tier, signal) {
      const marker = tier === 1 ? this.elements.tier1Marker : this.elements.tier2Marker;

      if (marker) {
        marker.classList.add('spend-and-earn__marker--celebrating');

        setTimeout(() => {
          marker.classList.remove('spend-and-earn__marker--celebrating');
        }, 600);
      }

      // Note: confetti is now triggered separately for better timing
      await this.delay(SpendAndEarn.TIMING.CELEBRATION_PAUSE, signal);
    }

    spawnGoldParticles(count = 8) {
      if (!this.elements.particles) return;

      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'gold-particle';

        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;

        this.elements.particles.appendChild(particle);

        const animation = particle.animate([
          {
            opacity: 0,
            transform: 'scale(0) translateY(0)'
          },
          {
            opacity: 1,
            transform: 'scale(1.5) translateY(-5px)',
            offset: 0.3
          },
          {
            opacity: 0.8,
            transform: 'scale(1) translateY(-10px)',
            offset: 0.7
          },
          {
            opacity: 0,
            transform: 'scale(0.5) translateY(-20px)'
          }
        ], {
          duration: 800 + Math.random() * 400,
          delay: Math.random() * 200,
          easing: 'ease-out'
        });

        animation.onfinish = () => particle.remove();
      }
    }

    triggerConfetti(tier = 1) {
      if (!this.elements.confetti) return;

      const originElement = tier === 1 ? this.elements.tier1Marker : this.elements.tier2Marker;
      if (!originElement) return;

      // Get the actual marker icon for more accurate centering
      const markerIcon = originElement.querySelector('.spend-and-earn__marker-icon');
      const originRect = markerIcon ? markerIcon.getBoundingClientRect() : originElement.getBoundingClientRect();

      const colors = [
        '#936347',
        '#B8860B',
        '#D4AF37',
        '#F4D03F',
        '#f3f2ec',
        '#E5DED5',
        '#C9A86C',
        '#8B5A3C'
      ];

      const confettiCount = tier === 2 ? 60 : 40;
      const explosionRadius = tier === 2 ? 250 : 180;
      const baseDuration = tier === 2 ? 1600 : 1200;

      const confettiRect = this.elements.confetti.getBoundingClientRect();
      const startX = originRect.left - confettiRect.left + (originRect.width / 2);
      const startY = originRect.top - confettiRect.top + (originRect.height / 2);

      for (let i = 0; i < confettiCount; i++) {
        const piece = document.createElement('div');
        piece.className = 'confetti-piece';

        piece.style.background = colors[Math.floor(Math.random() * colors.length)];
        piece.style.left = `${startX}px`;
        piece.style.top = `${startY}px`;

        const size = 6 + Math.random() * 8;
        piece.style.width = `${size}px`;
        piece.style.height = `${size}px`;
        piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';

        this.elements.confetti.appendChild(piece);

        const angle = Math.random() * 360 * (Math.PI / 180);
        const distance = Math.random() * explosionRadius;
        const randomX = Math.cos(angle) * distance;
        const randomY = Math.sin(angle) * distance;
        const rotation = 360 + Math.random() * 720;
        const duration = baseDuration + Math.random() * 600;
        const delay = Math.random() * 100;

        const animation = piece.animate([
          {
            transform: 'translate(0, 0) rotate(0deg) scale(1)',
            opacity: 1
          },
          {
            transform: `translate(${randomX}px, ${randomY}px) rotate(${rotation}deg) scale(0.2)`,
            opacity: 0
          }
        ], {
          duration,
          delay,
          easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fill: 'forwards'
        });

        animation.onfinish = () => piece.remove();
      }
    }

    updateUI(total) {
      this.updateMessage(total);
      this.updateMarkers(total);
      this.updateNextGiftPreview(total);
      this.updateRewardsList(total);
    }

    updateMessage(total, animate = true) {
      if (!this.elements.message) return;

      const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
      const { tier1Threshold, tier2Threshold } = this.config;
      const currentTier = this.getTier(total);

      let message = '';

      if (total === 0) {
        message = 'Add items to your cart to start earning <span class="spend-emphasis">free gifts!</span>';
      } else if (currentTier >= 2) {
        message = 'You\'ve unlocked all <span class="spend-emphasis">free gifts!</span>';
      } else if (currentTier === 1 || this.scaleMode === SpendAndEarn.SCALE_MODES.DUAL) {
        const remaining = tier2Threshold - total;
        message = `Spend <span class="spend-amount">${this.formatMoney(remaining, format)}</span> more for your next <span class="spend-emphasis">free gift!</span>`;
      } else {
        const remaining = tier1Threshold - total;
        const progress = (total / tier1Threshold) * 100;
        if (progress >= 85) {
          message = `So close! Just <span class="spend-amount">${this.formatMoney(remaining, format)}</span> away from a <span class="spend-emphasis">free gift!</span>`;
        } else {
          message = `Spend <span class="spend-amount">${this.formatMoney(remaining, format)}</span> more for a <span class="spend-emphasis">free gift!</span>`;
        }
      }

      if (this.elements.message.innerHTML === message) return;

      if (animate) {
        this.elements.message.style.opacity = '0';
        setTimeout(() => {
          this.elements.message.innerHTML = message;
          this.elements.message.style.opacity = '1';
        }, 400);
      } else {
        this.elements.message.innerHTML = message;
      }
    }

    updateNextGiftPreview(total, animate = false) {
      const currentTier = this.getTier(total);

      // Hide all next gift items first (without animation classes)
      if (this.elements.nextGiftTier1) {
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--visible');
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--unlocking');
        this.elements.nextGiftTier1.classList.remove('spend-and-earn__next-gift-item--entering');
      }
      if (this.elements.nextGiftTier2) {
        this.elements.nextGiftTier2.classList.remove('spend-and-earn__next-gift-item--visible');
        this.elements.nextGiftTier2.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
        this.elements.nextGiftTier2.classList.remove('spend-and-earn__next-gift-item--unlocking');
        this.elements.nextGiftTier2.classList.remove('spend-and-earn__next-gift-item--entering');
      }

      // Show the appropriate next gift based on current tier
      if (currentTier === 0) {
        // Working toward tier 1
        if (this.elements.nextGiftTier1) {
          this.elements.nextGiftTier1.classList.add('spend-and-earn__next-gift-item--visible');
        }
        if (this.elements.nextGift) {
          this.elements.nextGift.classList.remove('spend-and-earn__next-gift--hidden');
        }
      } else if (currentTier === 1) {
        // Tier 1 unlocked, working toward tier 2
        if (this.elements.nextGiftTier2) {
          this.elements.nextGiftTier2.classList.add('spend-and-earn__next-gift-item--visible');
          if (animate) {
            this.elements.nextGiftTier2.classList.add('spend-and-earn__next-gift-item--entering');
          }
        }
        if (this.elements.nextGift) {
          this.elements.nextGift.classList.remove('spend-and-earn__next-gift--hidden');
        }
      } else {
        // All tiers unlocked, hide next gift section
        if (this.elements.nextGift) {
          this.elements.nextGift.classList.add('spend-and-earn__next-gift--hidden');
        }
      }
    }

    async animateUnlock(tier, signal) {
      const nextGiftElement = tier === 1 ? this.elements.nextGiftTier1 : this.elements.nextGiftTier2;
      const rewardElement = tier === 1 ? this.elements.rewardTier1 : this.elements.rewardTier2;
      const nextNextGift = tier === 1 ? this.elements.nextGiftTier2 : null;

      // Use core unlock animation with conditional cleanup logic
      await this._performUnlockAnimation({
        lockedElement: nextGiftElement,
        rewardElement: rewardElement,
        initialDelay: 200,  // Breathing room after celebration
        collapseContainer: !nextNextGift,  // Collapse if this is the final tier
        onCleanup: async (lockedElement, rewardElement, signal) => {
          // Handle the next-gift item based on whether there's another tier
          if (nextNextGift) {
            // There's another tier - remove classes and show next gift
            if (lockedElement) {
              lockedElement.classList.remove('spend-and-earn__next-gift-item--visible');
              lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
              lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocking');
            }

            // Suspenseful pause before revealing next tier
            await this.delay(SpendAndEarn.TIMING.NEXT_LOCKED_ENTER_DELAY, signal);

            // Show the next tier's gift with entering animation
            nextNextGift.classList.add('spend-and-earn__next-gift-item--visible');
            nextNextGift.classList.add('spend-and-earn__next-gift-item--entering');

            // Clean up entering class after animation
            await this.delay(SpendAndEarn.TIMING.NEXT_LOCKED_ENTER_DURATION, signal);
            nextNextGift.classList.remove('spend-and-earn__next-gift-item--entering');
          } else {
            // No more tiers - container already collapsed in Phase 4
            // Just clean up locked item
            if (lockedElement) {
              lockedElement.classList.remove('spend-and-earn__next-gift-item--visible');
              lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocked-visible');
              lockedElement.classList.remove('spend-and-earn__next-gift-item--unlocking');
            }
          }
        }
      }, signal);
    }

    async animateBothTiersUnlock(signal) {
      // Dynamic parallel animation when both tiers unlock at once
      // Progress bar is already at 100% from animateToTier1WithReveal
      // This handles the gift transitions in parallel

      const tier1Next = this.elements.nextGiftTier1;
      const tier2Next = this.elements.nextGiftTier2;
      const tier1Reward = this.elements.rewardTier1;
      const tier2Reward = this.elements.rewardTier2;

      // Step 1: Start tier 1 unlocking animation (circle → check)
      if (tier1Next) {
        tier1Next.classList.add('spend-and-earn__next-gift-item--unlocking');
      }

      // Step 2: Tier 1 reward starts entering unlocked list (in parallel)
      if (tier1Reward) {
        tier1Reward.classList.add('spend-and-earn__reward--unlocked');
        tier1Reward.classList.add('spend-and-earn__reward--entering');
      }

      // Wait for tier 1 unlock animation
      await this.delay(500, signal);

      // Step 3: Hide tier 1 from locked, show tier 2 in locked
      if (tier1Next) {
        tier1Next.classList.remove('spend-and-earn__next-gift-item--visible');
        tier1Next.classList.remove('spend-and-earn__next-gift-item--unlocking');
      }

      // Show tier 2 in locked list (with locked styling - empty circle)
      if (tier2Next) {
        tier2Next.classList.add('spend-and-earn__next-gift-item--visible');
        tier2Next.classList.add('spend-and-earn__next-gift-item--entering');
      }

      // Wait for tier 2 to appear and tier 1 reward to settle
      await this.delay(600, signal);

      // Finalize tier 1 reward
      if (tier1Reward) {
        tier1Reward.classList.add('spend-and-earn__reward--revealed');
        tier1Reward.classList.remove('spend-and-earn__reward--entering');
      }

      // Clean up tier 2 entering animation
      if (tier2Next) {
        tier2Next.classList.remove('spend-and-earn__next-gift-item--entering');
      }

      // Step 4: Start tier 2 unlocking animation (circle → check)
      if (tier2Next) {
        tier2Next.classList.add('spend-and-earn__next-gift-item--unlocking');
      }

      // Unlock tier 2 marker
      if (this.elements.tier2Marker) {
        this.elements.tier2Marker.classList.remove('spend-and-earn__marker--locked');
        this.elements.tier2Marker.classList.remove('spend-and-earn__marker--approaching');
        this.elements.tier2Marker.classList.add('spend-and-earn__marker--unlocked');
      }

      this.spawnGoldParticles(12);

      // Celebrate tier 2
      this.transitionTo(SpendAndEarn.STATES.CELEBRATING);
      await this.celebrateTier(2, signal);

      // Step 5: Tier 2 transitions from locked to unlocked
      // Keep unlocking class to maintain faded state while container collapses
      if (this.elements.nextGift) {
        this.elements.nextGift.classList.add('spend-and-earn__next-gift--hidden');
      }

      // Tier 2 reward enters unlocked list
      if (tier2Reward) {
        tier2Reward.classList.add('spend-and-earn__reward--unlocked');
        tier2Reward.classList.add('spend-and-earn__reward--entering');

        setTimeout(() => {
          tier2Reward.classList.add('spend-and-earn__reward--revealed');
          tier2Reward.classList.remove('spend-and-earn__reward--entering');
        }, 900);
      }

      // Clean up tier 2 next gift after collapse
      setTimeout(() => {
        if (tier2Next) {
          tier2Next.classList.remove('spend-and-earn__next-gift-item--visible');
          tier2Next.classList.remove('spend-and-earn__next-gift-item--unlocking');
        }
      }, 900);
    }

    updateRewardsList(total) {
      const currentTier = this.getTier(total);

      // Tier 1 reward - only show in rewards list when unlocked
      if (this.elements.rewardTier1) {
        if (currentTier >= 1) {
          this.elements.rewardTier1.classList.add('spend-and-earn__reward--unlocked');
          this.elements.rewardTier1.classList.add('spend-and-earn__reward--revealed');
        } else {
          this.elements.rewardTier1.classList.remove('spend-and-earn__reward--unlocked');
          this.elements.rewardTier1.classList.remove('spend-and-earn__reward--revealed');
        }
      }

      // Tier 2 reward - only show in rewards list when unlocked
      if (this.elements.rewardTier2) {
        if (currentTier >= 2) {
          this.elements.rewardTier2.classList.add('spend-and-earn__reward--unlocked');
          this.elements.rewardTier2.classList.add('spend-and-earn__reward--revealed');
        } else {
          this.elements.rewardTier2.classList.remove('spend-and-earn__reward--unlocked');
          this.elements.rewardTier2.classList.remove('spend-and-earn__reward--revealed');
        }
      }
    }

    updateMarkers(total) {
      const currentTier = this.getTier(total);
      const approachingTier = this.getApproachingTier(total);

      if (this.elements.tier1Marker) {
        this.elements.tier1Marker.classList.remove('spend-and-earn__marker--approaching');

        if (currentTier >= 1) {
          this.elements.tier1Marker.classList.remove('spend-and-earn__marker--locked');
          this.elements.tier1Marker.classList.add('spend-and-earn__marker--unlocked');
        } else {
          this.elements.tier1Marker.classList.add('spend-and-earn__marker--locked');
          this.elements.tier1Marker.classList.remove('spend-and-earn__marker--unlocked');

          if (approachingTier === 1) {
            this.elements.tier1Marker.classList.add('spend-and-earn__marker--approaching');
          }
        }
      }

      if (this.elements.tier2Marker) {
        this.elements.tier2Marker.classList.remove('spend-and-earn__marker--approaching');

        if (currentTier >= 2) {
          this.elements.tier2Marker.classList.remove('spend-and-earn__marker--locked');
          this.elements.tier2Marker.classList.add('spend-and-earn__marker--unlocked');
        } else {
          this.elements.tier2Marker.classList.add('spend-and-earn__marker--locked');
          this.elements.tier2Marker.classList.remove('spend-and-earn__marker--unlocked');

          if (approachingTier === 2) {
            this.elements.tier2Marker.classList.add('spend-and-earn__marker--approaching');
          }
        }
      }
    }

    formatMoney(cents, format) {
      if (typeof window.formatMoney === 'function') {
        try {
          const formatted = window.formatMoney(cents, format);
          const temp = document.createElement('div');
          temp.innerHTML = formatted;
          return temp.textContent || temp.innerText || formatted;
        } catch {
          // Fall through to manual formatting
        }
      }

      if (typeof cents === 'string') {
        cents = parseFloat(cents);
      }

      if (isNaN(cents)) {
        cents = 0;
      }

      const value = (cents / 100).toFixed(2);
      const valueNoDecimals = Math.floor(cents / 100);
      const valueWithComma = value.replace('.', ',');
      const valueWithApostrophe = value.replace('.', "'");

      return format
        .replace('{{amount_with_comma_separator}}', valueWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', valueNoDecimals.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace('{{amount_no_decimals}}', valueNoDecimals.toString())
        .replace('{{amount_with_apostrophe_separator}}', valueWithApostrophe)
        .replace('{{amount}}', value);
    }
  }

  customElements.define('spend-and-earn', SpendAndEarn);
}
